import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  CalendarCheck, Pill, Bell, CheckCircle2, Phone, User, Clock, MapPin,
  Plus, X, MessageSquare, Sparkles, Send,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";

type Tab = "booking" | "rx" | "followup";

type ApptStatus = "pending" | "verified" | "rejected";
type Appt = { serial: string; patient: string; phone: string; time: string; chamber: string; status: ApptStatus };
const DEMO_APPTS: Appt[] = [
  { serial: "A-042", patient: "Rahim Uddin",   phone: "+8801711-234567", time: "10:30 AM", chamber: "Popular, Dhanmondi", status: "pending" },
  { serial: "A-043", patient: "Ayesha Khatun", phone: "+8801812-987654", time: "10:45 AM", chamber: "Popular, Dhanmondi", status: "pending" },
  { serial: "A-044", patient: "Karim Hossain", phone: "+8801911-555111", time: "11:00 AM", chamber: "Popular, Dhanmondi", status: "pending" },
];

const RX_TEMPLATES = [
  { name: "Fever + Cold", dx: "Viral URI", meds: [
    { n: "Napa 500mg",  d: "1+0+1", dur: "5 days", note: "After meal" },
    { n: "Fexo 120mg",  d: "0+0+1", dur: "5 days", note: "At night" },
  ]},
  { name: "Hypertension F/U", dx: "Essential HTN, controlled", meds: [
    { n: "Amlopin 5mg", d: "1+0+0", dur: "30 days", note: "Morning" },
    { n: "Ecosprin 75",  d: "0+1+0", dur: "30 days", note: "After lunch" },
  ]},
  { name: "Diabetes F/U", dx: "T2DM, HbA1c 7.4", meds: [
    { n: "Metformin 500", d: "1+0+1", dur: "30 days", note: "With meal" },
    { n: "Empa 10mg",     d: "1+0+0", dur: "30 days", note: "Morning" },
  ]},
];

export default function LivePreviews() {
  const { lang } = useLang();
  const isBn = lang === "bn";
  const [tab, setTab] = useState<Tab>("booking");

  const tabs: { id: Tab; icon: any; label: string; sub: string }[] = [
    { id: "booking",  icon: CalendarCheck, label: isBn ? "বুকিং যাচাই" : "Booking verification", sub: isBn ? "AI এজেন্ট ইনবক্স" : "AI Agent inbox" },
    { id: "rx",       icon: Pill,          label: isBn ? "প্রেসক্রিপশন ড্রাফট" : "Prescription drafting", sub: isBn ? "৩০ সেকেন্ডে Rx" : "30-second Rx" },
    { id: "followup", icon: Bell,          label: isBn ? "ফলো-আপ রিমাইন্ডার" : "Follow-up reminder", sub: isBn ? "WhatsApp টেমপ্লেট" : "WhatsApp template" },
  ];

  return (
    <section id="live-demo" className="max-w-7xl mx-auto mt-24 md:mt-40 px-4 md:px-6">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 text-xs font-medium tracking-widest uppercase text-blue-600 mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          {isBn ? "লাইভ ডেমো" : "Live demo"}
        </div>
        <h2 className="text-4xl md:text-5xl leading-[1.05] font-semibold text-gray-900">
          {isBn ? "সরাসরি চেষ্টা করে দেখুন" : "Try the flows right here"}
        </h2>
        <p className="mt-4 text-gray-600">
          {isBn
            ? "সাইন-ইন ছাড়াই ডেমো ডেটা দিয়ে বুকিং যাচাই, Rx ড্রাফট আর ফলো-আপ রিমাইন্ডার তৈরি করে দেখুন।"
            : "No sign-in needed. Interact with real demo data to verify a booking, draft a prescription, and schedule a WhatsApp follow-up."}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {tabs.map((tt) => {
          const active = tab === tt.id;
          const Icon = tt.icon;
          return (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border transition ${
                active
                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                  : "bg-white text-gray-700 border-gray-200 hover:border-blue-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium">{tt.label}</span>
              <span className={`hidden md:inline text-xs ${active ? "text-blue-100" : "text-gray-500"}`}>· {tt.sub}</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {tab === "booking"  && <BookingDemo  isBn={isBn} />}
        {tab === "rx"       && <RxDemo       isBn={isBn} />}
        {tab === "followup" && <FollowUpDemo isBn={isBn} />}
      </div>
    </section>
  );
}

/* ---------------- Booking verification ---------------- */
function BookingDemo({ isBn }: { isBn: boolean }) {
  const [rows, setRows] = useState(DEMO_APPTS);
  const [filter, setFilter] = useState("");

  const filtered = rows.filter(
    (r) =>
      r.patient.toLowerCase().includes(filter.toLowerCase()) ||
      r.phone.includes(filter) ||
      r.serial.toLowerCase().includes(filter.toLowerCase())
  );

  const verify = (serial: string) =>
    setRows((rs) => rs.map((r) => (r.serial === serial ? { ...r, status: "verified" } : r)));
  const reject = (serial: string) =>
    setRows((rs) => rs.map((r) => (r.serial === serial ? { ...r, status: "rejected" } : r)));

  return (
    <div className="p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-sm font-semibold text-gray-900">{isBn ? "AI-বুক করা অ্যাপয়েন্টমেন্ট" : "AI-booked appointments"}</div>
          <div className="text-xs text-gray-500">{isBn ? "ডেমো ডেটা — যাচাই করে ফাইনাল সিরিয়াল দিন" : "Demo data — verify to lock the serial"}</div>
        </div>
        <Input
          className="w-full sm:w-64"
          placeholder={isBn ? "নাম / ফোন / সিরিয়াল দিয়ে ফিল্টার" : "Filter by name / phone / serial"}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium">{isBn ? "সিরিয়াল" : "Serial"}</th>
              <th className="text-left px-3 py-2 font-medium">{isBn ? "রোগী" : "Patient"}</th>
              <th className="text-left px-3 py-2 font-medium hidden md:table-cell">{isBn ? "ফোন" : "Phone"}</th>
              <th className="text-left px-3 py-2 font-medium">{isBn ? "সময়" : "Time"}</th>
              <th className="text-left px-3 py-2 font-medium hidden md:table-cell">{isBn ? "চেম্বার" : "Chamber"}</th>
              <th className="text-right px-3 py-2 font-medium">{isBn ? "অ্যাকশন" : "Action"}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.serial} className="border-t border-gray-100">
                <td className="px-3 py-3 font-mono text-xs">{r.serial}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-700 grid place-items-center text-xs font-semibold">
                      {r.patient.charAt(0)}
                    </div>
                    <span className="text-gray-900">{r.patient}</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-600 hidden md:table-cell"><span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span></td>
                <td className="px-3 py-3 text-gray-700"><span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{r.time}</span></td>
                <td className="px-3 py-3 text-gray-600 hidden md:table-cell"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{r.chamber}</span></td>
                <td className="px-3 py-3">
                  <div className="flex justify-end gap-2">
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" className="h-8" onClick={() => reject(r.serial)}>
                          <X className="w-3.5 h-3.5 mr-1" /> {isBn ? "না" : "Reject"}
                        </Button>
                        <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" onClick={() => verify(r.serial)}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {isBn ? "নিশ্চিত" : "Verify"}
                        </Button>
                      </>
                    )}
                    {r.status === "verified" && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{isBn ? "যাচাইকৃত" : "Verified"}</Badge>
                    )}
                    {r.status === "rejected" && (
                      <Badge variant="secondary" className="bg-rose-100 text-rose-700 hover:bg-rose-100">{isBn ? "বাতিল" : "Rejected"}</Badge>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">{isBn ? "কোনো ফলাফল নেই" : "No results"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-gray-500">
        {isBn
          ? "লাইভ অ্যাপে — WhatsApp এজেন্ট বুকিং করলে এখানেই ডুপ্লিকেট সিরিয়াল চেক আর কনফার্মেশন মেসেজ যায়।"
          : "In the live app the WhatsApp agent posts bookings here, checks for duplicate serials, and sends a confirmation card to the patient."}
      </p>
    </div>
  );
}

/* ---------------- Rx drafting ---------------- */
function RxDemo({ isBn }: { isBn: boolean }) {
  const [tplIdx, setTplIdx] = useState(0);
  const [dx, setDx] = useState(RX_TEMPLATES[0].dx);
  const [meds, setMeds] = useState(RX_TEMPLATES[0].meds);
  const [advice, setAdvice] = useState(isBn ? "প্রচুর পানি পান করুন। ৫ দিন পর রিভিউ।" : "Plenty of fluids. Review after 5 days.");

  const loadTpl = (i: number) => {
    setTplIdx(i);
    setDx(RX_TEMPLATES[i].dx);
    setMeds(RX_TEMPLATES[i].meds);
  };
  const addMed = () => setMeds((m) => [...m, { n: "", d: "1+0+1", dur: "5 days", note: "" }]);
  const removeMed = (i: number) => setMeds((m) => m.filter((_, idx) => idx !== i));
  const updateMed = (i: number, k: keyof typeof meds[number], v: string) =>
    setMeds((m) => m.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  return (
    <div className="grid md:grid-cols-2">
      {/* Editor */}
      <div className="p-5 md:p-6 border-b md:border-b-0 md:border-r border-gray-100 bg-gray-50/40">
        <div className="text-sm font-semibold text-gray-900 mb-1">{isBn ? "প্রেসক্রিপশন এডিটর" : "Prescription editor"}</div>
        <div className="text-xs text-gray-500 mb-4">{isBn ? "টেমপ্লেট বাছুন, তারপর কাস্টমাইজ করুন" : "Pick a template, then customise"}</div>

        <div className="flex flex-wrap gap-2 mb-4">
          {RX_TEMPLATES.map((t, i) => (
            <button
              key={t.name}
              onClick={() => loadTpl(i)}
              className={`text-xs px-3 py-1.5 rounded-full border ${tplIdx === i ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-200 hover:border-blue-300"}`}
            >
              {t.name}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">{isBn ? "রোগ নির্ণয়" : "Diagnosis"}</Label>
            <Input value={dx} onChange={(e) => setDx(e.target.value)} className="mt-1" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">{isBn ? "ঔষধ" : "Medications"}</Label>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addMed}>
                <Plus className="w-3 h-3 mr-1" /> {isBn ? "যোগ" : "Add"}
              </Button>
            </div>
            <div className="space-y-2">
              {meds.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                  <Input className="col-span-5 h-8 text-xs" placeholder={isBn ? "ঔষধ" : "Medicine"} value={m.n} onChange={(e) => updateMed(i, "n", e.target.value)} />
                  <Input className="col-span-3 h-8 text-xs font-mono" placeholder="1+0+1" value={m.d} onChange={(e) => updateMed(i, "d", e.target.value)} />
                  <Input className="col-span-3 h-8 text-xs" placeholder="5 days" value={m.dur} onChange={(e) => updateMed(i, "dur", e.target.value)} />
                  <button className="col-span-1 text-gray-400 hover:text-rose-500" onClick={() => removeMed(i)}>
                    <X className="w-3.5 h-3.5 mx-auto" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">{isBn ? "উপদেশ" : "Advice"}</Label>
            <Textarea rows={2} value={advice} onChange={(e) => setAdvice(e.target.value)} className="mt-1 text-sm" />
          </div>
        </div>
      </div>

      {/* Live Rx paper */}
      <div className="p-5 md:p-6">
        <div className="text-sm font-semibold text-gray-900 mb-3">{isBn ? "লাইভ প্রিভিউ" : "Live preview"}</div>
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 min-h-[380px]" style={{ fontFamily: "Caveat, cursive" }}>
          <div className="flex items-start justify-between border-b border-gray-200 pb-3" style={{ fontFamily: "Inter, sans-serif" }}>
            <div>
              <div className="text-base font-semibold text-gray-900">Dr. Ayesha Rahman</div>
              <div className="text-xs text-gray-500">MBBS, FCPS (Medicine) · BMDC A-12345</div>
            </div>
            <div className="text-right text-xs text-gray-500">
              <div>Rx #24-0842</div>
              <div>{new Date().toLocaleDateString()}</div>
            </div>
          </div>

          <div className="mt-4 text-lg">
            <div className="text-gray-500 text-xs uppercase tracking-wider" style={{ fontFamily: "Inter, sans-serif" }}>Diagnosis</div>
            <div className="text-gray-900">{dx || "—"}</div>
          </div>

          <div className="mt-4">
            <div className="text-3xl text-blue-700">℞</div>
            <ol className="mt-1 space-y-2 text-lg text-gray-900 pl-2">
              {meds.map((m, i) => (
                <li key={i} className="border-b border-dashed border-gray-100 pb-1.5">
                  <div><span className="font-semibold">{i + 1}. {m.n || "—"}</span> — <span className="font-mono text-base">{m.d}</span></div>
                  <div className="text-sm text-gray-600 pl-4" style={{ fontFamily: "Inter, sans-serif" }}>{m.dur}{m.note ? ` · ${m.note}` : ""}</div>
                </li>
              ))}
            </ol>
          </div>

          {advice && (
            <div className="mt-4">
              <div className="text-gray-500 text-xs uppercase tracking-wider" style={{ fontFamily: "Inter, sans-serif" }}>Advice</div>
              <div className="text-gray-900 text-base">{advice}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Follow-up reminder ---------------- */
function FollowUpDemo({ isBn }: { isBn: boolean }) {
  const [patient, setPatient] = useState("Rahim Uddin");
  const [phone, setPhone] = useState("+8801711-234567");
  const [days, setDays] = useState(7);
  const [channel, setChannel] = useState<"wa" | "sms">("wa");
  const [scheduled, setScheduled] = useState(false);

  const dueDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString(isBn ? "bn-BD" : "en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }, [days, isBn]);

  const message = isBn
    ? `প্রিয় ${patient}, আপনার ফলো-আপ ${dueDate} তারিখে নির্ধারিত। সিরিয়ালের জন্য এই মেসেজে "CONFIRM" লিখে পাঠান। — Dr. Ayesha Rahman`
    : `Dear ${patient}, your follow-up is due on ${dueDate}. Reply "CONFIRM" to book a serial. — Dr. Ayesha Rahman`;

  return (
    <div className="grid md:grid-cols-2">
      <div className="p-5 md:p-6 border-b md:border-b-0 md:border-r border-gray-100 bg-gray-50/40">
        <div className="text-sm font-semibold text-gray-900 mb-1">{isBn ? "ফলো-আপ সেটআপ" : "Schedule a follow-up"}</div>
        <div className="text-xs text-gray-500 mb-4">{isBn ? "রোগীর WhatsApp-এ স্বয়ংক্রিয় রিমাইন্ডার যাবে" : "Automated reminder will hit the patient's WhatsApp"}</div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">{isBn ? "রোগীর নাম" : "Patient name"}</Label>
            <div className="relative mt-1">
              <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" value={patient} onChange={(e) => { setPatient(e.target.value); setScheduled(false); }} />
            </div>
          </div>
          <div>
            <Label className="text-xs">{isBn ? "ফোন" : "Phone"}</Label>
            <div className="relative mt-1">
              <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" value={phone} onChange={(e) => { setPhone(e.target.value); setScheduled(false); }} />
            </div>
          </div>
          <div>
            <Label className="text-xs">{isBn ? `কত দিন পর: ${days}` : `Remind in: ${days} day(s)`}</Label>
            <input
              type="range" min={1} max={30} value={days}
              onChange={(e) => { setDays(Number(e.target.value)); setScheduled(false); }}
              className="w-full accent-blue-600 mt-2"
            />
            <div className="flex justify-between text-[10px] text-gray-500 mt-1"><span>1d</span><span>15d</span><span>30d</span></div>
          </div>
          <div>
            <Label className="text-xs">{isBn ? "চ্যানেল" : "Channel"}</Label>
            <div className="flex gap-2 mt-1">
              {(["wa", "sms"] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => { setChannel(c); setScheduled(false); }}
                  className={`flex-1 py-2 rounded-lg text-sm border ${channel === c ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-200"}`}
                >
                  {c === "wa" ? "WhatsApp" : "SMS"}
                </button>
              ))}
            </div>
          </div>

          <Button className="w-full bg-blue-600 hover:bg-blue-700 mt-2" onClick={() => setScheduled(true)}>
            <Send className="w-4 h-4 mr-2" />
            {isBn ? "রিমাইন্ডার সেট করুন" : "Schedule reminder"}
          </Button>

          {scheduled && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-3 py-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              {isBn ? `রিমাইন্ডার ${dueDate}-এ পাঠানো হবে` : `Reminder queued for ${dueDate}`}
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp preview */}
      <div className="p-5 md:p-6 bg-gradient-to-b from-emerald-50/50 to-white">
        <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-600" />
          {isBn ? "WhatsApp প্রিভিউ" : "WhatsApp preview"}
        </div>
        <div className="mx-auto max-w-sm rounded-2xl bg-[#e5ddd5] border border-emerald-100 p-3 shadow-sm">
          <div className="text-[11px] text-gray-500 text-center mb-2">{dueDate} · {isBn ? "নির্ধারিত" : "scheduled"}</div>
          <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm text-sm text-gray-900 leading-relaxed">
            {message}
            <div className="text-[10px] text-gray-400 text-right mt-1">10:00 AM ✓✓</div>
          </div>
          <div className="mt-2 flex justify-end gap-1.5">
            <button className="text-xs px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-700">{isBn ? "নিশ্চিত" : "CONFIRM"}</button>
            <button className="text-xs px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-700">{isBn ? "রিশিডিউল" : "RESCHEDULE"}</button>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500 text-center">
          {isBn ? "লাইভ অ্যাপে — Twilio/Meta ফলব্যাক সহ স্বয়ংক্রিয়ভাবে পাঠানো হয়।" : "Live app sends via Twilio / Meta with a WhatsApp deep-link fallback."}
        </p>
      </div>
    </div>
  );
}
